import { useRef, useEffect, useMemo } from 'react';
// NOTE: Keep the scene calm and stable; avoid busy particle effects.
import { useFrame } from '@react-three/fiber';
import { Group } from 'three';
import { useSpring, animated } from '@react-spring/three';
import ToolCard from './ToolCard';
import type { Tool } from '../types';

interface CarouselSceneProps {
  tools: Tool[];
  activeTool: Tool | null;
  onToolSelect: (tool: Tool | null) => void;
  onCardReachedCenter: () => void;
  onCardReturnedToRing: (returnedToolId: number) => void;
  isCardActuallyCentered: boolean;
  isAnyToolProcessActive: boolean;
  pendingTool?: Tool | null;
}

// Helper to get parent rotation safely
function getParentRotationY(group: Group | null): number {
  return group?.parent?.rotation.y || 0;
}

function AnimatedToolCardWrapper({
  tool,
  originalPosition,
  originalRotation,
  isWrapperActive,
  isActuallyCenteredInApp,
  isAnyToolProcessActiveInApp,
  onSelect,
  onCenterComplete,
  onReturnComplete,
}: {
  tool: Tool;
  originalPosition: [number, number, number];
  originalRotation: [number, number, number];
  isWrapperActive: boolean;
  isActuallyCenteredInApp: boolean;
  isAnyToolProcessActiveInApp: boolean;
  onSelect: () => void;
  onCenterComplete: () => void;
  onReturnComplete: (returnedToolId: number) => void;
}) {
  const cardGroupRef = useRef<Group>(null);

  // We animate a "phase" value instead of direct transform properties for the active state
  // 0 = In Ring (Local Slot)
  // 1 = In Center (World Locked)
  const [{ phase }, api] = useSpring(() => ({
    phase: 0,
    config: { mass: 1.2, tension: 200, friction: 32 },
  }));

  // Track opacity separately
  const [{ opacity }, opacityApi] = useSpring(() => ({
    opacity: 1,
    config: { mass: 0.7, tension: 260, friction: 28 },
  }));

  useEffect(() => {
    if (isWrapperActive) {
      if (isActuallyCenteredInApp) {
        // Go to Center (Phase 1)
        api.start({
          phase: 1,
          config: { mass: 1.2, tension: 200, friction: 32 },
          onRest: () => {
            onCenterComplete();
          },
        });
        opacityApi.start({ opacity: 1 });
      } else {
        // Return to ring (Active tool but not centered, e.g. switching)
        api.start({
          phase: 0,
          config: { mass: 1.5, tension: 180, friction: 40 },
          onRest: () => onReturnComplete(tool.id),
        });
        opacityApi.start({ opacity: 1 });
      }
    } else {
      // Inactive
      if (isAnyToolProcessActiveInApp) {
        // Smoothly disappear all other cards
        api.start({ phase: 0 });
        opacityApi.start({
          opacity: 0,
          config: { mass: 0.6, tension: 280, friction: 26 },
        });
      } else {
        // Return to Ring (Phase 0, Opacity 1)
        api.start({
          phase: 0,
          config: { mass: 1.5, tension: 180, friction: 40 },
          onRest: () => onReturnComplete(tool.id),
        });
        opacityApi.start({
          opacity: 1,
          config: { mass: 0.8, tension: 220, friction: 28 },
        });
      }
    }
  }, [isWrapperActive, isActuallyCenteredInApp, isAnyToolProcessActiveInApp, api, opacityApi, onCenterComplete, onReturnComplete, tool.id]);

  useFrame(() => {
    if (!cardGroupRef.current) return;

    const currentPhase = phase.get();
    const currentOpacity = opacity.get();
    const parentRotationY = getParentRotationY(cardGroupRef.current);
    const PI = Math.PI;

    // Disappear / hide inactive cards completely when another tool is active
    if (!isWrapperActive && isAnyToolProcessActiveInApp) {
      if (currentOpacity <= 0.01) {
        cardGroupRef.current.visible = false;
        return;
      }
      cardGroupRef.current.visible = true;
      const shrinkScale = Math.max(0.001, currentOpacity);
      cardGroupRef.current.scale.setScalar(shrinkScale);
      return;
    }

    cardGroupRef.current.visible = true;

    // 1. Calculate "Slot" State (Local)
    // 2. Calculate "Center" State (World Locked -> Converted to Local)
    const x_world = 0;
    const z_world = 3.5; // Target distance in front of camera

    const sinN = Math.sin(-parentRotationY);
    const cosN = Math.cos(-parentRotationY);

    const targetLocalX = x_world * cosN - z_world * sinN;
    const targetLocalZ = x_world * sinN + z_world * cosN;
    const targetLocalY = 0; // Center Y

    // 3. Interpolate Position
    cardGroupRef.current.position.x = originalPosition[0] + (targetLocalX - originalPosition[0]) * currentPhase;
    cardGroupRef.current.position.y = originalPosition[1] + (targetLocalY - originalPosition[1]) * currentPhase;
    cardGroupRef.current.position.z = originalPosition[2] + (targetLocalZ - originalPosition[2]) * currentPhase;

    // Rotation interpolation
    const startRotY = originalRotation[1];
    const endRotY = -parentRotationY; // Face camera

    let delta = endRotY - startRotY;
    while (delta > PI) delta -= 2 * PI;
    while (delta < -PI) delta += 2 * PI;

    cardGroupRef.current.rotation.y = startRotY + delta * currentPhase;

    // Active Card Scale
    const startScale = 1;
    const endScale = 1.25;

    const currentScale = startScale + (endScale - startScale) * currentPhase;
    cardGroupRef.current.scale.setScalar(currentScale);
  });

  return (
    <animated.group
      ref={cardGroupRef}
    // Opacity is handled by spring directly as it's independent of transforms
    >
      <ToolCard
        tool={tool}
        position={[0, 0, 0]}
        rotation={[0, 0, 0]}
        isActive={isWrapperActive && isActuallyCenteredInApp}
        onClick={onSelect}
        opacity={opacity}
      />
    </animated.group>
  );
}

export default function CarouselScene({
  tools,
  activeTool,
  onToolSelect,
  onCardReachedCenter,
  onCardReturnedToRing,
  isCardActuallyCentered,
  isAnyToolProcessActive,
  pendingTool
}: CarouselSceneProps) {
  const groupRef = useRef<Group>(null);

  /* Removed unused spring logic */

  // Target rotation for snappy navigation
  const [rotationSpring, rotationApi] = useSpring(() => ({
    rotationY: Math.PI / 2, // Start rotated +90deg to bring Index 0 (Right) to Front (+Z)
    config: { mass: 1.5, tension: 140, friction: 40 } // Snappy but smooth
  }));

  const currentRotationIndex = useRef(0);

  // Sync rotation when activeTool OR pendingTool changes
  // We prioritize pendingTool to start visual rotation IMMEDIATELY upon selection
  useEffect(() => {
    // Keep background stable while a tool is open,
    // but when switching (pendingTool) bring the target card to the front.
    const targetTool = pendingTool || (!isAnyToolProcessActive ? activeTool : null);

    if (targetTool) {
      const targetIndex = tools.findIndex(t => t.id === targetTool.id);
      if (targetIndex !== -1) {
        // Calculate the target rotation index (negative because ArrowRight decrements)
        currentRotationIndex.current = -targetIndex;

        const angleStep = (2 * Math.PI) / tools.length;
        // Apply offset to center the card (move from X axis to Z axis)
        rotationApi.start({
          rotationY: currentRotationIndex.current * angleStep + Math.PI / 2,
          // If we're switching tools, snap so the new card doesn't fly in from the back.
          immediate: !!pendingTool,
        });
      }
    }
  }, [activeTool, pendingTool, tools, rotationApi, isAnyToolProcessActive]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isAnyToolProcessActive) return;

      if (e.key === 'ArrowRight') {
        currentRotationIndex.current -= 1; // Move right means rotate counter-clockwise effectively
        const angleStep = (2 * Math.PI) / tools.length;
        rotationApi.start({ rotationY: currentRotationIndex.current * angleStep + Math.PI / 2 });
      } else if (e.key === 'ArrowLeft') {
        currentRotationIndex.current += 1;
        const angleStep = (2 * Math.PI) / tools.length;
        rotationApi.start({ rotationY: currentRotationIndex.current * angleStep + Math.PI / 2 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tools.length, isAnyToolProcessActive, rotationApi]);

  useFrame(() => {
    if (groupRef.current) {
      // Apply spring rotation only when idle (keeps background stable while a tool is open)
      if (!isAnyToolProcessActive || !!pendingTool) {
        const currentY = rotationSpring.rotationY.get();
        groupRef.current.rotation.y = currentY;
      }

      // Always keep the ring stable (no float/breathe). Motion comes from explicit user actions.
      groupRef.current.position.y = 0;
      groupRef.current.scale.setScalar(1);
    }
  });

  const radius = 5;
  const angleStep = (2 * Math.PI) / tools.length;

  const cardTransforms = useMemo(() => {
    return tools.map((tool, index) => {
      const angle = index * angleStep;
      const x = radius * Math.cos(angle);
      const z = radius * Math.sin(angle);
      return {
        tool,
        originalPosition: [x, 0, z] as [number, number, number],
        originalRotation: [0, -angle + Math.PI / 2, 0] as [number, number, number],
      };
    });
  }, [tools, radius, angleStep]);

  if (!Array.isArray(tools) || tools.length === 0) {
    return null;
  }

  return (
    <animated.group ref={groupRef}>
      {/* Interaction Mesh Removed */}
      {cardTransforms.map(({ tool, originalPosition, originalRotation }) => {
        if (!tool?.id || !tool?.name || !tool?.icon) {
          return null;
        }
        const isCurrentCardTheAppActiveTool = tool.id === activeTool?.id;

        return (
          <AnimatedToolCardWrapper
            key={tool.id}
            tool={tool}
            originalPosition={originalPosition}
            originalRotation={originalRotation}
            isWrapperActive={isCurrentCardTheAppActiveTool}
            isActuallyCenteredInApp={isCardActuallyCentered}
            isAnyToolProcessActiveInApp={isAnyToolProcessActive}
            onSelect={() => onToolSelect(tool)}
            onCenterComplete={() => {
              if (isCurrentCardTheAppActiveTool) onCardReachedCenter();
            }}
            onReturnComplete={(returnedToolId) => {
              if (isCurrentCardTheAppActiveTool) onCardReturnedToRing(returnedToolId);
            }}
          />
        );
      })}

      {/* Handheld Camera Rig */}
      <CameraRig isAnyToolProcessActive={isAnyToolProcessActive} />
    </animated.group>

  );
}

function CameraRig({ isAnyToolProcessActive }: { isAnyToolProcessActive: boolean }) {
  useFrame((state) => {
    // Keep camera stable while a tool is open/switching.
    if (isAnyToolProcessActive) {
      state.camera.position.x += (0 - state.camera.position.x) * 0.05;
      state.camera.position.y += (0 - state.camera.position.y) * 0.05;
      state.camera.rotation.z += (0 - state.camera.rotation.z) * 0.05;
      state.camera.lookAt(0, 0, 0);
      return;
    }

    // Subtle breathing/sway (idle only) - Reduced amplitude
    const t = state.clock.elapsedTime;
    state.camera.position.y = Math.sin(t * 0.4) * 0.015; // Slower, less movement
    state.camera.rotation.z = Math.sin(t * 0.15) * 0.0015;

    // Mouse parallax (idle only)
    const { x, y } = state.pointer;
    state.camera.position.x += (x * 0.04 - state.camera.position.x) * 0.015;
    state.camera.position.y += (y * 0.04 - state.camera.position.y) * 0.015;

    state.camera.lookAt(0, 0, 0);
  });
  return null;
}